import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Container,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalBody,
} from 'reactstrap';
import PageBreadcrumb from '../../Components/Common/PageBreadcrumb';
import DataTable from 'react-data-table-component';
import ActionButton from '../../Components/Common/ActionButton';
import DeleteModal from '../../Components/Common/DeleteModal';
import ListPager from '../../Components/Common/ListPager';
import {
  ActiveFilterChips,
  ClearFiltersButton,
  ListSearch,
} from '../../Components/Common/ListFilters';
import { EmptyState, SkeletonRows } from '../../Components/Common/ListStates';
import notify, { MESSAGES } from '../../utils/notify';
import '../TenderOpening/components/TenderOpening.css';
import axios from 'axios';
import { api } from '../../config';
import { getAuthToken } from '../../auth/tokenvalidation';
import { FormGrid, TextAreaField, TextField } from '../../Components/Form';

const LocationList = () => {
  const token = getAuthToken();

  const [data, setData] = useState({ items: [] });
  const [deleteModal, setDeleteModal] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [modalLocation, setModalLocation] = useState(false);
  const [locationFormData, setLocationFormData] = useState({
    name: null,
    description: null,
    id: null,
  });

  const [filterText, setFilterText] = useState('');
  const [search, setSearch] = useState('');
  const requestRef = useRef(0);
  const [loading, setLoading] = useState(false);
  const startEntry = totalRows === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const endEntry = Math.min(currentPage * rowsPerPage, totalRows);

  const handleDeleteModal = id => {
    const selectedRow = data.items.find(item => item.id === id);
    setInvoice(selectedRow);
    setDeleteModal(true);
  };

  const handleDeleteInvoice = async () => {
    if (invoice) {
      try {
        const response = await fetch(`${api.API_URL}/location/${invoice.id}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            errorData.message || `HTTP error! status: ${response.status}`
          );
        }

        notify.success('Location deleted successfully.');
        setDeleteModal(false);
        setInvoice(null);
        fetchData(currentPage, rowsPerPage, token, search);
      } catch (err) {
        notify.apiError(err, 'delete location');
      }
    }
  };

  const columns = [
    {
      id: 'name',
      name: 'Location Name',
      selector: row => row.name,
      sortable: true,
      width: '40%',
      cell: row => (
        <span className='to-title' title={String((row.name || '-') ?? '')}>
          {(row.name || '-') ?? '-'}
        </span>
      ),
    },

    {
      id: 'description',
      name: 'Description',
      selector: row => row.description,
      sortable: true,
      width: '45%',
      cell: row => (
        <span
          className='pv-desc'
          title={String((row.description || '-') ?? '')}
        >
          {(row.description || '-') ?? '-'}
        </span>
      ),
    },

    {
      name: <span className='text-center w-100'>Action</span>,
      width: '15%',
      center: true,
      cell: row => (
        <div className='d-flex gap-1 justify-content-center w-100'>
          <ActionButton
            variant='icon'
            size='sm'
            icon='ri-edit-line'
            label='Edit'
            onClick={() => handleUpdateModal(row.id)}
          />
          <ActionButton
            variant='icon'
            size='sm'
            danger
            icon='ri-delete-bin-line'
            label='Delete'
            confirm={false}
            onClick={() => handleDeleteModal(row.id)}
          />
        </div>
      ),
    },
  ];

  document.title = 'Data Location';

  const handleChange = e => {
    const { name, value } = e.target;
    setLocationFormData({ ...locationFormData, [name]: value });
  };

  // Debounce typing so each keystroke is not a request.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(filterText.trim());
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [filterText]);

  useEffect(() => {
    fetchData(currentPage, rowsPerPage, token, search);
  }, [currentPage, rowsPerPage, token, search]);

  const handleFilterChange = e => {
    setFilterText(e.target.value);
    // Clearing applies at once; typing waits for the debounce.
    if (!e.target.value) {
      setSearch('');
      setCurrentPage(1);
    }
  };

  const clearFilter = () => {
    setFilterText('');
    setSearch('');
    setCurrentPage(1);
  };

  const fetchData = async (currentPage, rowsPerPage, token, searchText) => {
    const reqId = ++requestRef.current;
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        PageNumber: currentPage.toString(),
        PageSize: rowsPerPage.toString(),
      });
      if (searchText) {
        queryParams.append('Search', searchText);
      }

      const response = await fetch(
        `${api.API_URL}/location?${queryParams.toString()}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          credentials: 'same-origin',
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (reqId !== requestRef.current) return; // stale response

      // Handle different possible response structures
      const items =
        result?.data?.items || result?.items || result?.data || result || [];
      const totalItems =
        result?.data?.totalItems || result?.totalItems || items.length;

      setData({
        items: Array.isArray(items) ? items : [],
      });
      setTotalRows(totalItems);
    } catch (error) {
      if (reqId !== requestRef.current) return;
      notify.apiError(error, 'load data');
      setData({ items: [] });
      setTotalRows(0);
    } finally {
      if (reqId === requestRef.current) setLoading(false);
    }
  };

  const handlePageChange = useCallback(page => {
    setCurrentPage(page);
  }, []);

  const handlePerRowsChange = useCallback(newPerPage => {
    setRowsPerPage(newPerPage);
    setCurrentPage(1);
  }, []);

  const handleNewModal = () => {
    setLocationFormData({
      name: null,
      description: null,
      id: null,
    });
    locationModal();
  };

  const handleUpdateModal = async (mid = null) => {
    await fetchLocationDetail(mid);
    locationModal();
  };

  const locationModal = () => {
    setModalLocation(!modalLocation);
  };

  const selectedFieldsLocation = {
    name: locationFormData.name,
    description: locationFormData.description,
  };

  const handleAddDataLocation = async () => {
    try {
      await axios.post(`${api.API_URL}/location`, selectedFieldsLocation, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      notify.success('Location added successfully.');
    } catch (error) {
      notify.apiError(error, 'save data');
    } finally {
      fetchData(currentPage, rowsPerPage, token, search);
    }
  };

  const fetchLocationDetail = async mid => {
    try {
      const response = await axios.get(`${api.API_URL}/location/${mid}`, {
        withXSRFToken: true,
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: `application/json`,
        },
      });
      // Interceptor global (src/helpers/api_helper.js) sudah meng-unwrap
      // `response.data`, jadi `response` adalah envelope { success, message, data }.
      // Kupas satu lapis lagi, tetap toleran kalau bentuknya berbeda.
      const data = response?.data?.data ?? response?.data ?? response;
      setLocationFormData({
        name: data.name || null,
        description: data.description || null,
        id: data.id || null,
      });
    } catch (error) {
      notify.apiError(error, 'fetch location details');
    }
  };

  const handleUpdDataLocation = async mid => {
    try {
      await axios.put(
        `${api.API_URL}/location/${mid}`,
        selectedFieldsLocation,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      notify.success(MESSAGES.updated);
    } catch (error) {
      notify.apiError(error, 'save data');
    } finally {
      fetchData(currentPage, rowsPerPage, token, search);
    }
  };

  const filterChips = [
    filterText && {
      key: 'search',
      label: 'Search',
      value: filterText,
      onRemove: () => handleFilterChange({ target: { value: '' } }),
    },
  ].filter(Boolean);

  return (
    <React.Fragment>
      <div className='page-content'>
        <DeleteModal
          show={deleteModal}
          onDeleteClick={() => handleDeleteInvoice()}
          onCloseClick={() => setDeleteModal(false)}
          title='Delete this location?'
          message='It will be permanently deleted. This cannot be undone.'
          subject={invoice?.name}
          confirmLabel='Delete location'
        />
        <Container fluid>
          <div className='to-page email-wrapper d-lg-flex gap-1 mx-n4 mt-n4 p-1'>
            <div className='email-content minimal-border'>
              <div className='p-4'>
                <div className='to-hero mb-4'>
                  <PageBreadcrumb
                    fallback={{ label: 'Location', to: '/apps-location-view' }}
                  />

                  <div className='d-flex align-items-center gap-3 flex-wrap'>
                    <span className='to-hero-icon'>
                      <i className='ri-map-pin-line' aria-hidden='true' />
                    </span>
                    <div className='flex-grow-1'>
                      <h4 className='to-hero-title'>Location</h4>
                      <p className='to-hero-subtitle'>
                        Maintain the locations available when preparing
                        procurements.
                      </p>
                    </div>
                    <div className='d-flex gap-2 flex-wrap'>
                      <button
                        type='button'
                        className='to-hero-btn'
                        onClick={() =>
                          fetchData(currentPage, rowsPerPage, token, search)
                        }
                      >
                        <i className='ri-refresh-line' aria-hidden='true' />
                        Refresh
                      </button>
                      <button
                        type='button'
                        className='to-hero-btn to-hero-btn--solid'
                        onClick={() => handleNewModal()}
                      >
                        <i className='ri-add-line' aria-hidden='true' />
                        Create New Location
                      </button>
                    </div>
                  </div>
                </div>

                <div className='to-panel'>
                  <div className='to-toolbar'>
                    <ListSearch
                      value={filterText}
                      onChange={handleFilterChange}
                      placeholder='Search locations...'
                      ariaLabel='Search locations'
                    />
                    <ClearFiltersButton
                      count={filterChips.length}
                      onClick={clearFilter}
                    />
                    <span className='to-toolbar-meta'>
                      {loading
                        ? 'Loading...'
                        : `Showing ${startEntry}–${endEntry} of ${totalRows} locations`}
                    </span>
                  </div>

                  <ActiveFilterChips chips={filterChips} />

                  <div className='to-table-wrap'>
                    <DataTable
                      columns={columns}
                      data={data.items}
                      highlightOnHover
                      persistTableHead
                      defaultSortFieldId='name'
                      defaultSortAsc
                      progressPending={loading}
                      progressComponent={<SkeletonRows />}
                      noDataComponent={
                        <EmptyState
                          title='No location found'
                          emptyHint='No locations have been registered yet. Locations added here become selectable across the system.'
                          filteredHint={`No location matches "${filterText}".`}
                          hasFilters={Boolean(filterText)}
                          onClear={clearFilter}
                          action={{
                            label: 'Create New Location',
                            onClick: handleNewModal,
                          }}
                        />
                      }
                      customStyles={{
                        headCells: {
                          style: { paddingLeft: '12px', paddingRight: '12px' },
                        },
                        cells: {
                          style: {
                            paddingLeft: '12px',
                            paddingRight: '12px',
                            wordBreak: 'break-word',
                          },
                        },
                        table: { style: { width: '100%' } },
                      }}
                    />
                  </div>

                  <ListPager
                    currentPage={currentPage}
                    rowsPerPage={rowsPerPage}
                    totalRows={totalRows}
                    isLoading={loading}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handlePerRowsChange}
                  />
                </div>
              </div>
            </div>
          </div>

          <Modal
            isOpen={modalLocation}
            role='dialog'
            autoFocus={true}
            centered
            id='addLocationModal'
            toggle={locationModal}
            data-bs-backdrop='static'
            size='md'
          >
            <ModalHeader
              className='bg-soft-info'
              toggle={() => {
                setModalLocation(!modalLocation);
              }}
            >
              <span className='modal-title text-info fw-bold'>
                {locationFormData.id !== null
                  ? 'Edit Location'
                  : 'Add Location'}
              </span>
            </ModalHeader>

            <ModalBody>
              <FormGrid columns={1}>
                <TextField
                  label='Location name'
                  name='name'
                  placeholder='e.g. Balongan Terminal'
                  value={locationFormData.name}
                  onChange={handleChange}
                />
                <TextAreaField
                  label='Description'
                  name='description'
                  rows={5}
                  value={locationFormData.description}
                  onChange={handleChange}
                />
              </FormGrid>
            </ModalBody>

            <ModalFooter>
              <ActionButton
                variant='secondary'
                size='sm'
                icon='ri-close-line'
                onClick={() => {
                  setModalLocation(!modalLocation);
                }}
              >
                Close
              </ActionButton>
              <ActionButton
                variant='primary'
                size='sm'
                icon='ri-save-line'
                onClick={async () => {
                  await (locationFormData.id !== null
                    ? handleUpdDataLocation(locationFormData.id)
                    : handleAddDataLocation());
                  setModalLocation(false);
                }}
              >
                {locationFormData.id !== null ? 'Update' : 'Save'}
              </ActionButton>
            </ModalFooter>
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default LocationList;
